import React, { useEffect, useState } from 'react';
import Tabs, { Item } from 'devextreme-react/tabs';
import { useLocation, useSearchParams } from 'react-router-dom';
import { formatMessage } from 'devextreme/localization';

import GlobalLoader from '../loader/GlobalLoader';
import CustomerInfos from '../customer-infos/CustomerInfos';
import CustomerGrid from '../customer-grid/CustomerGrid';
import UserGrid from '../user-grid/UserGrid';
import RoleGroupGrid from '../rolegroup-grid/RoleGroupGrid';
import WorkspaceGrid from '../workspace-grid/WorkspaceGrid';
import DataSourceGrid from '../datasource-grid/DataSourceGrid';
import PathCredentialGrid from '../path-credential-grid/PathCredentialGrid';
import AgentGrid from '../agent-grid/AgentGrid';
import CustomerSso from '../customer-sso/CustomerSso';

import {
    ScreenMyCompany,
    ScreenCompanies,
    ScreenGlobalUsers,
    ScreenRoleGroups,
    ScreenWorkspaces,
    ScreenDataSources,
    ScreenPathCredentials,
    ScreenAgents,
    ScreenWorkspaceUsers,
    ScreenSso
} from '../../classes/consts/Screen';
import { UserRightForbidden } from '../../classes/consts/UserRight';

import UserSessionApi from '../../api/UserSessionApi';
import UserRightDto from '../../classes/dtos/usersessionmodule/UserRightDto';
import { useAuth } from '../../contexts/auth';

import './settings-page.scss';

interface SettingsPageProps {
    screenConst: number;
    titleMessageKey: string;
}

export default function SettingsPage({ screenConst, titleMessageKey }: Readonly<SettingsPageProps>) {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [currentTab, setCurrentTab] = useState<number>(0);
    const [visibleScreens, setVisibleScreens] = useState<Array<boolean>>(new Array<boolean>(10));

    function changePage(index: number): void {
        setCurrentTab(index);
        setSearchParams({ tab: index.toString() }, { replace: true });
    }

    const { sessionDetail } = useAuth();

    const [userRightDto, setUserRightDto] = useState<UserRightDto | null>(null);

    useEffect(() => {
        if (userRightDto === null) {
            UserSessionApi.GetUserRightsAsync(screenConst)
                .then(detail => {
                    setUserRightDto(detail.Result);

                    const visibleScreens = new Array<boolean>(10);

                    visibleScreens[0] = detail.Result.GetUserRight(ScreenMyCompany) !== UserRightForbidden;
                    visibleScreens[1] = detail.Result.GetUserRight(ScreenCompanies) !== UserRightForbidden;
                    visibleScreens[2] = detail.Result.GetUserRight(ScreenGlobalUsers) !== UserRightForbidden;
                    visibleScreens[3] = detail.Result.GetUserRight(ScreenWorkspaces) !== UserRightForbidden;
                    visibleScreens[4] = detail.Result.GetUserRight(ScreenRoleGroups) !== UserRightForbidden;
                    visibleScreens[5] = detail.Result.GetUserRight(ScreenDataSources) !== UserRightForbidden;
                    visibleScreens[6] = detail.Result.GetUserRight(ScreenPathCredentials) !== UserRightForbidden;
                    visibleScreens[7] = detail.Result.GetUserRight(ScreenAgents) !== UserRightForbidden;
                    visibleScreens[8] = detail.Result.GetUserRight(ScreenWorkspaceUsers) !== UserRightForbidden;
                    visibleScreens[9] = detail.Result.GetUserRight(ScreenSso) !== UserRightForbidden;

                    setVisibleScreens(visibleScreens);

                    const tabFromUrl = Number(searchParams.get('tab'));
                    const requestedTab = !Number.isNaN(tabFromUrl) && searchParams.has('tab')
                        ? tabFromUrl
                        : location.state?.activeTab;
                    const initialTab = requestedTab !== undefined && visibleScreens[requestedTab]
                        ? requestedTab
                        : visibleScreens.indexOf(true);

                    setCurrentTab(initialTab);
                    setSearchParams({ tab: initialTab.toString() }, { replace: true });
                });
        }
    }, [userRightDto]);

    if (userRightDto === null) {
        return <GlobalLoader />;
    }

    return (
        <div className="settings-page-container">
            <div className="settings-page-internal-container">
                <div className="settings-page-title-container">
                    <h2>{formatMessage(titleMessageKey)}</h2>
                </div>
                <Tabs width='100%' stylingMode='secondary' onSelectedIndexChange={changePage} selectedIndex={currentTab}>
                    <Item text={formatMessage('SettingsScreenCustomerInfos_Title')} visible={visibleScreens[0]} />
                    <Item text={formatMessage('SettingsScreenCustomers_Title')} visible={visibleScreens[1]} />
                    <Item text={formatMessage('SettingsScreenGlobalUsers_Title')} visible={visibleScreens[2]} />
                    <Item text={formatMessage('SettingsScreenWorkspaces_Title')} visible={visibleScreens[3]} />
                    <Item text={formatMessage('SettingsScreenRoleGroups_Title')} visible={visibleScreens[4]} />
                    <Item text={formatMessage('SettingsScreenDataSources_Title')} visible={visibleScreens[5]} />
                    <Item text={formatMessage('SettingsScreenPathCredentials_Title')} visible={visibleScreens[6]} />
                    <Item text={formatMessage('SettingsScreenAgents_Title')} visible={visibleScreens[7]} />
                    <Item text={formatMessage('SettingsScreenWorkspaceUsers_Title')} visible={visibleScreens[8]} />
                    <Item text={formatMessage('SettingsScreenSso_Title')} visible={visibleScreens[9]} />
                </Tabs>
                {currentTab === 0 && <CustomerInfos />}
                {currentTab === 1 && <CustomerGrid />}
                {currentTab === 2 && <UserGrid EnterpriseMode={sessionDetail?.EnterpriseMode ?? false} GlobalUser={true} WorkspaceUser={false} />}
                {currentTab === 3 && <WorkspaceGrid />}
                {currentTab === 4 && <RoleGroupGrid />}
                {currentTab === 5 && <DataSourceGrid />}
                {currentTab === 6 && <PathCredentialGrid />}
                {currentTab === 7 && <AgentGrid />}
                {currentTab === 8 && <UserGrid EnterpriseMode={sessionDetail?.EnterpriseMode ?? false} GlobalUser={false} WorkspaceUser={true} />}
                {currentTab === 9 && <CustomerSso />}
            </div>
        </div>
    );
}
